import theme_icons from '../../icons.js';
import { MediaUploaderGeneric } from '../../components/image-picker-generic.js';
import { AdderGeneric } from '../../components/adder-generic.js';

const { __ } = wp.i18n;
const { registerBlockType } = wp.blocks;
const { useEffect } = wp.element;
const { RichText, InspectorControls } = wp.blockEditor;
const { Button, PanelBody, TextControl, ToggleControl, RangeControl } = wp.components;

const iconcards = {
  title: 'Icon Cards',
  icon: theme_icons.greenheart,
  category: 'greenheart',
  supports: {
    align: ["wide", "full"],
  },
  attributes: {
    isAdding: {
      type: 'boolean',
      default: false
    },
    isEditing: {
      type: 'number',
      default: -1
    },
    title: {
      type: 'string',
      default: ''
    },
    text: {
      type: 'string',
      default: ''
    },
    icon: {
      type: 'object',
      default: {}
    },
    link: {
      type: 'string',
      default: ''
    },
    isBlank: {
      type: 'boolean',
      default: false
    },
    cards: {
      type: 'array',
      default: []
    },
    currentId: {
      type: 'number',
      default: 0
    },
    colNum: {
      type: 'number',
      default: 3
    }
  },

  edit({ attributes, setAttributes }) {
    const { isAdding, isEditing, title, text, icon, link, isBlank, cards, currentId, colNum } = attributes;

    useEffect(() => {
      let i = getNextId();
      setAttributes({ currentId: i });
    }, []);

    const getNextId = () => {
      let i = 1;
      while (cards.filter((card) => card.id === i).length > 0) { i++; }
      return i;
    }

    function onTitleChange(newTitle) {
      setAttributes({ title: newTitle });
    }

    function onTextChange(newText) {
      setAttributes({ text: newText });
    }

    function onIconChange(newIcon) {
      setAttributes({ icon: newIcon });
    }

    function onLinkChange(newLink) {
      setAttributes({ link: newLink });
    }

    const resetAddCardForm = () => {
      let nextId = getNextId();
      setAttributes({
        isAdding: false,
        isEditing: -1,
        title: '',
        text: '',
        icon: {},
        link: ''
      });
    }

    const addCard = () => {
      const nextId = getNextId();
      setAttributes({
        currentId: nextId,
        isAdding: true,
        isEditing: -1,
      });
    };

    const editCard = (id) => {
      const card = cards.find((card) => card.id === id);
      setAttributes({
        title: card.title,
        text: card.text,
        icon: card.icon,
        link: card.link,
        isBlank: card.isBlank,
        currentId: id,
        isEditing: id,
      });
    };

    const saveCard = () => {
      const newCard = {
        title: title,
        text: text,
        icon: icon,
        link: link,
        isBlank: isBlank,
        id: currentId,
      };

      const cardIndex = cards.findIndex((card) => card.id === currentId);
      let newCards;

      if (cardIndex !== -1) {
        newCards = [...cards];
        newCards[cardIndex] = newCard;
      } else {
        newCards = [...cards, newCard];
      }

      setAttributes({ cards: newCards });
      resetAddCardForm();
    };

    const deleteCard = (id) => {
      const newCards = cards.filter((card) => card.id !== id);
      setAttributes({ cards: newCards });
    }

    const moveCard = (index, direction) => {
      if (
        (direction === 'left' && index === 0) ||
        (direction === 'right' && index === cards.length - 1)
      ) {
        return; // Don't move if at the edges
      }

      const newCards = [...cards];
      const newIndex = direction === 'left' ? index - 1 : index + 1;
      [newCards[index], newCards[newIndex]] = [newCards[newIndex], newCards[index]];
      setAttributes({ cards: newCards });
    };

    const displayCards = () => {
      return (
        <div className="cards row">
          {cards.map((card, index) => {
            return (
              <div key={index} className="col">
                <div className="block card">
                  <div className="card-img-top">
                    <img className="card-icon" src={card.icon.mediaUrl} alt={card.title + " icon"} />
                  </div>
                  <div className="card-body">
                    <h3 className="card-title">{card.title}</h3>
                    <div className="card-text">{card.text}</div>
                  </div>
                  <div className="card-controls">
                    <Button className="button secondary" onClick={() => deleteCard(card.id)}>Delete</Button>
                    <Button className="button primary" onClick={() => editCard(card.id)}>Edit</Button>
                    <Button
                      className="button order-button"
                      onClick={() => moveCard(index, 'left')}
                      disabled={index === 0}
                    >
                      ⬅️
                    </Button>
                    <Button
                      className="button order-button"
                      onClick={() => moveCard(index, 'right')}
                      disabled={index === cards.length - 1}
                    >
                      ➡️
                    </Button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    return ([
      <InspectorControls>
        <PanelBody title="Card Settings">
          <p><strong>Cards per row</strong></p>
          <RangeControl
            label="Cards per row"
            value={colNum}
            onChange={(newValue) => setAttributes({ colNum: newValue })}
            min={1}
            max={4}
          />

          {link &&
            <>
              <p><strong>Open in New Tab?</strong></p>
              <ToggleControl
                label="Open link in a new tab"
                checked={isBlank}
                onChange={(newValue) => setAttributes({ isBlank: newValue })}
              />
            </>
          }

        </PanelBody>
      </InspectorControls>,
      <div className="rbt-iconcards">
        {(isAdding || isEditing > 0) ?
          <div className="rbt-iconcards-form">
            <div className="card-wrap">
              <div className="icon-area">
                <MediaUploaderGeneric photo={icon} updateImage={onIconChange} />
              </div>
              <div className="title-area">
                <RichText
                  tagName="h3"
                  className="card-title"
                  value={title}
                  onChange={onTitleChange}
                  placeholder="Card Title"
                />
              </div>
              <div className="text-area">
                <RichText
                  tagName="p"
                  value={text}
                  onChange={onTextChange}
                  placeholder="Card Text"
                />
              </div>
              <div className="link-area">
                <TextControl
                  label="Link URL (optional)"
                  value={link}
                  onChange={onLinkChange}
                />
              </div>
            </div>
            {title.length > 0 && icon.hasOwnProperty('mediaUrl') &&
              <Button className="button primary" onClick={saveCard}>Save Card</Button>
            }
            <Button className="button secondary" onClick={resetAddCardForm}>Cancel</Button>
          </div>
          :
          <div className="rbt-iconcards-cards">
            {displayCards()}
            <AdderGeneric onAdd={addCard} />
          </div>
        }
      </div>
    ]);
  },

  save({ attributes }) {
    const { cards, colNum } = attributes;
    const colClass = `col-${colNum}`;
    return (
      <div className="block">
        <div className={"cards row " + colClass}>
          {cards.map((card, index) => (
            <div key={index} className="col">
              {card.link ? (
                <a
                  href={card.link}
                  title={card.title}
                  className="card hover-shadow"
                  target={card.isBlank ? "_blank" : "_self"}
                  rel="noopener"
                >
                  <div className="card-img-top">
                    <img alt="icon" loading="lazy" src={card.icon.mediaUrl} />
                  </div>
                  <div className="card-body">
                    <h3 className="card-title">{card.title}</h3>
                    <div className="card-text">{card.text}</div>
                  </div>
                </a>
              ) : (
                <div className="card hover-shadow">
                  <div className="card-img-top">
                    <img alt="icon" loading="lazy" src={card.icon.mediaUrl} />
                  </div>
                  <div className="card-body">
                    <h3 className="card-title">{card.title}</h3>
                    <div className="card-text">{card.text}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
};

registerBlockType('rbt/iconcards', iconcards);
export default iconcards;